/**
 * The version bar above the grid: which version of the plan you are looking at, and
 * whether the grid still matches it.
 *
 *   • Saved   — nothing changed since the live version: names it ("v3 · Committed ·
 *               Pre-kickoff").
 *   • Unsaved — the grid has moved on: "Version 4 · unsaved changes since …", and the
 *               version it started from.
 *   • New     — no version saved yet.
 *
 * Every status works the same way (versions are not a Won-only feature), so the bar shows
 * on every plan. "New version" saves the grid as it stands; the old versions stay on the
 * Versions tab, where any of them can be restored.
 */

import { GitBranch } from "lucide-react";
import { CommitVersionButton } from "@/components/plans/CommitVersionButton";
import { Button } from "@/components/ui/button";
import { usePlanVersions } from "@/hooks/use-plan-governance";
import { formatDateYMD } from "@/lib/format";
import type { PlanVersionListItemView, ResourcePlanDetailView } from "@/schemas/resource-plans";

function versionName(version: PlanVersionListItemView): string {
  return version.commitLabel === null ? version.label : `${version.label} · ${version.commitLabel}`;
}

export function PlanVersionBar({
  plan,
  onOpenVersions,
}: {
  plan: ResourcePlanDetailView;
  onOpenVersions: () => void;
}) {
  const { data: versions } = usePlanVersions(plan.id);
  const live = versions?.find((v) => v.isLive) ?? null;
  const unsavedSince = plan.uncommittedChangesAt ?? null;
  const unsaved = plan.hasUnsavedChanges ?? unsavedSince !== null;

  const headline =
    live !== null && !unsaved ? versionName(live) : `Version ${plan.versionNumber ?? 1}`;

  const since =
    unsavedSince === null
      ? "Unsaved changes"
      : `Unsaved changes since ${formatDateYMD(unsavedSince)}`;
  const detail =
    live === null
      ? versions === undefined
        ? null
        : "Not saved as a version yet"
      : unsaved
        ? `${since} · based on ${versionName(live)}`
        : "Saved";

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-muted/40 px-3 py-2">
      <div className="flex min-w-0 items-center gap-2 text-sm">
        <GitBranch className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="font-medium text-foreground">{headline}</span>
        {detail !== null && (
          <span
            className={
              unsaved && live !== null
                ? "text-warning-700 dark:text-warning-400"
                : "text-muted-foreground"
            }
          >
            {detail}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="ghost" onClick={onOpenVersions}>
          All versions
        </Button>
        <CommitVersionButton plan={plan} variant="outline" />
      </div>
    </div>
  );
}
